import { useCallback, useEffect, useState, ChangeEvent } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Tab, Tabs, Button } from '@pwc/appkit-react';
import { useQueryClient } from 'react-query';
import { SelectionChangedEvent } from 'ag-grid-community';

import SharedHeader from '../../../shared/displayComponents/header/header.component';
import { useQueryStatusEntityInformationDiagnostics } from '../../../shared/queries/entityInformationDiagnostics';
import { globalContextSelector } from '../../../shared/store/selectors';
import Loading from '../../../shared/displayComponents/loading.component';
import { EntityInformationRefreshRequest, GlobalContextParams } from '../../../../../common/types';
import { QueryKeys } from '../../../shared/queries';
import { showConfirmModal } from '../../../shared/confirmModal/store/actions';
import { useMutationRefreshEntityInformation } from '../../../shared/mutations/entityInformationDiagnostics';

import styles from './entityInformationDiagnostics.module.scss';
import { RecentRefreshes, AllEntities } from './tables';
import { Entity, TabProps } from './types';

enum TabsTypes {
  RecentRefreshes = 'RecentRefreshes',
  AllEntities = 'Entities',
}

const tabsDefinitions = [
  { type: TabsTypes.RecentRefreshes, label: 'Recent Refreshes' },
  { type: TabsTypes.AllEntities, label: 'Entities' },
];

const getActiveTabComponent = (activeTab: TabsTypes, props: TabProps) => {
  switch (activeTab) {
    case TabsTypes.RecentRefreshes:
      return <RecentRefreshes />;
    case TabsTypes.AllEntities:
      return <AllEntities {...props} />;
    default:
      throw new Error('Unsupported tab type');
  }
};

const EntityInformationDiagnostics = () => {
  const dispatch = useDispatch();

  const [activeTab, setActiveTab] = useState(TabsTypes.RecentRefreshes);
  const [selectedEntities, setSelectedEntities] = useState<Entity[]>([]);
  const queryClient = useQueryClient();

  const handleTabChange = useCallback((_: ChangeEvent, tab: TabsTypes) => setActiveTab(tab), []);
  const {
    mutateAsync: refreshEntityInformation,
    isLoading: isRefreshingEntityInformation,
  } = useMutationRefreshEntityInformation();

  const { taxYear, period }: GlobalContextParams = useSelector(globalContextSelector).params;
  const { data: status, isFetching: isFetchingStatus } = useQueryStatusEntityInformationDiagnostics(
    {
      taxYear,
      enabled: Boolean(taxYear && period),
    },
  );

  const isLoading = isRefreshingEntityInformation || isFetchingStatus;

  useEffect(() => {
    setSelectedEntities([]);
    queryClient.resetQueries(QueryKeys.EntityInformationDiagnostics.AllEntities);
  }, [isRefreshingEntityInformation, isFetchingStatus]);

  const onRefreshClick = useCallback(() => {
    queryClient.resetQueries(QueryKeys.EntityInformationDiagnostics.Status);
    switch (activeTab) {
      case TabsTypes.AllEntities:
        queryClient.resetQueries(QueryKeys.EntityInformationDiagnostics.AllEntities);
        return;
      case TabsTypes.RecentRefreshes:
        queryClient.resetQueries(QueryKeys.EntityInformationDiagnostics.RecentRefreshes);
        return;
      default:
        throw new Error('Unsupported tab type');
    }
  }, [queryClient, activeTab]);

  const onSelectionChanged = useCallback(
    (e: SelectionChangedEvent) => {
      if (activeTab === TabsTypes.AllEntities) {
        setSelectedEntities(e.api.getSelectedRows());
      }
    },
    [activeTab],
  );

  const fullSyncClick = async (details: Entity) => {
    const entityInformationRefreshRequest: EntityInformationRefreshRequest = {
      taxYear,
      entityId: details?.orgId,
      entityOpsUpdatedDate: details?.entityOpsUpdatedOn?.toString() ?? '',
      reportingPeriodId: details.reportingPeriod,
      filingGroupId: null,
      orgType: details.orgType,
    };
    console.log(entityInformationRefreshRequest);
    await refreshEntityInformation({
      entity: details?.orgId,
      entityInformationRefreshRequest,
    });
  };

  const handleSync = () => {
    dispatch(
      showConfirmModal({
        title: 'Refresh Entity Ops Information',
        text: `Are you sure you want to sync these entites with Entity Ops?`,
        confirmCallback: async () => {
          selectedEntities.forEach(entity => fullSyncClick(entity));
        },
      }),
    );
  };

  return (
    <>
      <SharedHeader title="Refresh Entity Information Diagnostics">
        <div className={styles.subheader}>
          <span> Refreshes In The Last 24 Hours </span>
          <Loading small isLoading={isLoading}>
            <span> Total: {status?.total} </span>
            <span> Complete: {status?.completed} </span>
            <span> In Progress: {status?.inProgress} </span>
            <span> Unsuccessful: {status?.unsuccessful} </span>
          </Loading>
        </div>
      </SharedHeader>
      <div className={`row ${styles.mainPage}`}>
        <div className={styles.tabSection}>
          <Tabs className={styles.tabs} value={activeTab} size="md" onChange={handleTabChange}>
            {tabsDefinitions.map(({ label, type }) => (
              <Tab id={type} value={type} label={label} key={type} />
            ))}
          </Tabs>
          <div className={styles.buttons}>
            <Button
              size="lg"
              onClick={handleSync}
              disabled={isLoading || selectedEntities.length == 0}
              className={styles.refreshDataBtn}
              kind="secondary"
            >
              Sync Entities
            </Button>
            <Button
              size="lg"
              onClick={onRefreshClick}
              disabled={isLoading}
              className={styles.refreshDataBtn}
              kind="secondary"
            >
              Reload Table
            </Button>
          </div>
        </div>
        <div className={styles.tableFormat}>
          {' '}
          {getActiveTabComponent(activeTab, { onSelectionChanged, isLoading })}
        </div>
      </div>
    </>
  );
};

export default EntityInformationDiagnostics;
